/*
 * This file provides helper functions for LUX, Speedcurve's Real User Monitoring
 * Product: https://speedcurve.com/features/lux/
 *
 * Because Booking is a single page app, we need to do some work to manually define
 * page transitions. See documentation on how to set up LUX in a SPA:
 *
 * https://support.speedcurve.com/using-lux/using-lux-in-a-single-page-app-spa
 *
 * Additional details from email from Speedcurve support team:
 *
 * For the very first application page load, you should set LUX.auto = false and
 * call LUX.send(), but you should not call LUX.init(). This is because LUX.init()
 * resets the LUX internal state that is used to calculate the time between page
 * loads, so you would get inaccurate measurements if you call it on the very first
 * load.
 *
 * For a single page application, the order of events should be:
 *
 * 1. User opens the initial page. You set LUX.auto = false and set LUX.label.
 * 2. When the page is fully loaded, you call LUX.send().
 * 3. User clicks to go to another page.
 * 4. You call LUX.init() to mark the beginning of the page load. You can set
 * LUX.label before or after this.
 * 5. You detect when the next page has fully loaded, and call LUX.send().
 * 6. Repeat 3-5 for each subsequent page.
 */
import * as Sentry from '@sentry/browser';

/*
 * Call this function before transition to next page, i.e, user clicks navigation link
 */
export function startLUXPageTransition() {
    const { LUX } = window;

    if (LUX && typeof LUX.init === 'function') {
        try {
            LUX.init();
        } catch (error) {
            Sentry.captureException(error);
        }
    }
}

// This is to prevent the same transition from being recorded multiple times
const luxTransitions = {};
/*
 * Call this function once page has 'fully loaded'. Fully loaded is defined as when
 * main interactable content has been rendered to the page. Usually, this means calling
 * the function in the componentDidMount lifecycle method of the outermost view component
 * on the page. For pages that require an AJAX request to be returned before meaningful
 * content can be rendered, such as review and book, wait to call until the meaningful
 * content has been commited to the DOM
 */

/**
 * @param {string} pageName
 * @param {Object} location - React Router location object
 */
export function completeLUXPageTransition(pageName, location = {}) {
    const { LUX } = window;
    const pageLoadContext =
        location.action === 'PUSH' ? 'Transition' : 'Landing';
    const fullPageName = `${pageName} - ${pageLoadContext}`;
    if (LUX && typeof LUX.send === 'function') {
        if (!luxTransitions[fullPageName]) {
            try {
                luxTransitions[fullPageName] = true;
                LUX.label = fullPageName;
                LUX.send();
            } catch (error) {
                Sentry.captureException(error);
            }
        }
    }
}

/*
 * Call this function after user converts to correlate conversions with page performance
 */
export function logConversionToLUX() {
    if (window.LUX && typeof window.LUX.addData === 'function') {
        try {
            window.LUX.addData('booking.confirmed', 1);
        } catch (error) {
            Sentry.captureException(error);
        }
    }
}
